<template>
  <div class="app-wrap">
    <!-- Background -->
    <div class="bg-gradient"></div>
    <div class="bg-texture" aria-hidden="true"></div>
    <div class="bg-glow" aria-hidden="true"></div>
        <br /><br /><br /><br />
    <!-- Header -->
<!-- Header -->
<!-- Header -->
 
<!-- Header -->
<header class="nav glass" role="banner">
  <!-- LEFT -->
  <div class="brand">
    <span class="logo">🎬</span>
    <h1>Pitch Analyst AI</h1>
  </div>

  <!-- RIGHT -->
  <div class="right">
    <label class="select sm">
      <span>Pitch Style</span>
      <select v-model="pitchStyle" aria-label="Pitch style">
        <option>Elevator</option>
        <option>Producer Room</option>
        <option>Festival Lab</option>
        <option>Writer-Director Deck</option>
        <option>Investor (ROI)</option>
        <option>Studio (Commercial)</option>
      </select>
    </label>
    <button class="secondary" @click="startPathway" :disabled="loading">Build Q&amp;A</button>
    <button class="secondary" @click="startPitchSimulation" :disabled="loading">Start Simulation</button>
  </div>
</header>




<!-- KPI strip (no inline styles; uses .kpi-grid/.kpi + progressbar classes) -->
<section class="pane glass">
  <div class="kpi-grid">
    <div class="kpi">
      <div>
        <div class="kpi-title muted">Average</div>
        <div class="kpi-value">{{ totalScore }}%</div>
      </div>
      <span class="pill" :class="{ on: totalScore>=80 }">{{ totalScore>=80 ? 'PASS' : 'WORK' }}</span>
    </div>

    <div class="kpi">
      <div>
        <div class="kpi-title muted">Step</div>
        <div class="kpi-value">
          <span v-if="hasPathway">{{ currentIndex + 1 }} / {{ pathway.length }}</span>
          <span v-else>—</span>
        </div>
      </div>
      <span class="pill">{{ pitchStyle }}</span>
    </div>

    <div class="kpi">
      <div>
        <div class="kpi-title muted">Live Match</div>
        <div class="kpi-value">
          {{ matchPercent }}%
        </div>
      </div>
      <span class="pill" :class="{ on: coachOverlayOpen }">Coach</span>
    </div>

    <div class="kpi">
      <div>
        <div class="kpi-title muted">WPM</div>
        <div class="kpi-value">{{ liveWpm || 0 }}</div>
      </div>
      <span class="pill" :class="{ on: recognizing }">{{ recognizing ? 'Listening' : (ttsSpeaking ? 'Speaking' : 'Idle') }}</span>
    </div>

    <div class="kpi">
      <div>
        <div class="kpi-title muted">Timer</div>
        <div class="kpi-value">
          <span v-if="coachOverlayOpen">{{ overlayCountdown }}s</span>
          <span v-else>{{ allowedSecs }}s</span>
        </div>
      </div>
      <span class="pill">{{ fillerCount }} fillers</span>
    </div>
  </div>

  <!-- Progress bar -->
  <div v-if="hasPathway" class="mt">
    <div class="kpi-title muted" style="margin-bottom:6px;">Progress</div>
    <div class="progressbar">
      <div
        class="progressbar-fill"
        :style="{ width: ((currentIndex+1)/Math.max(1,pathway.length))*100 + '%' }"></div>
    </div>
  </div>
</section>

<main class="main">
  <!-- LEFT: Intake / Script -->
  <section class="pane glass">
    <h1 class="section-title">Project Intake</h1>

    <div class="grid">
      <label>Title
        <input v-model.trim="scriptTitle" placeholder="The Quiet Storm" />
      </label>
      <label>Author
        <input v-model.trim="scriptAuthor" placeholder="Alex Rivera" />
      </label>
      <label>Genre
        <select v-model="scriptGenre" aria-label="Genre">
          <option v-for="g in genres" :key="g" :value="g">{{ g }}</option>
        </select>
      </label>
      <label>Pages
        <input type="number" min="1" v-model.number="scriptPages" />
      </label>
    </div>

    <div class="grid">
      <label>Upload excerpt (.txt/.md)
        <input color="#000" type="file" accept=".txt,.md,.markdown" @change="e => onScriptUpload(e.target.files[0])" />
      </label>
      <label>Pitch Style (set in header)
        <input disabled :value="pitchStyle" />
      </label>
    </div>

    <label>Paste excerpt (first 4–6k chars used)
      <textarea
        v-model="scriptText"
        rows="8"
        placeholder="Paste a pivotal scene, synopsis, or opening pages"></textarea>
    </label>

    <div class="row mt">
      <button class="ghost" @click="analyzeScriptContext" :disabled="loading">Analyze (Producer Mindset)</button>
      <span class="muted" v-if="loading">Working…</span>
    </div>

    <div v-if="answer" class="answer" v-html="answer"></div>
    <div v-if="suggestedPitchUpdatedAt" class="muted small">Last suggested pitch generated at {{ new Date(suggestedPitchUpdatedAt).toLocaleString() }}</div>

    <div class="row mt">
      <button class="ghost" @click="savePitch">💾 Save</button>
      <button class="ghost" @click="openLoadDialog = !openLoadDialog">{{ openLoadDialog ? 'Close' : 'Open' }} Saved</button>
      <button class="danger ghost" v-if="currentPitch" @click="deletePitch(currentPitch.id)">Delete Current</button>
    </div>

    <div v-if="openLoadDialog" class="saved glass mt">
      <div class="save-row" v-for="p in savedPitches" :key="p.id">
        <div class="save-meta">
          <div class="title">{{ p.scriptTitle || '(untitled)' }}</div>
          <div class="muted">
            {{ p.style }} • {{ p.scriptGenre || '—' }} • Updated {{ new Date(p.updatedAt || p.id).toLocaleString() }}
          </div>
        </div>
        <div class="save-actions">
          <button class="secondary sm" @click="editPitch(p)">Load</button>
          <button class="danger ghost sm" @click="deletePitch(p.id)">Delete</button>
        </div>
      </div>
      <div v-if="!savedPitches.length" class="muted">No saved sessions yet.</div>
    </div>
  </section>

  <!-- RIGHT: Voice + Q&A -->
  <section class="pane glass">
    <div class="flexbar">
      <h1 class="section-title">Voice + Pathway</h1>
      <div class="mic">
        <button
          class="mic-btn"
          :class="[
            stepColorClass,
            { on: recognizing, disabled: !voiceSupported, pulse: expectingAnswer && !recognizing }
          ]"
          :title="recognizing ? 'Stop listening' : 'Speak'"
          :aria-pressed="String(recognizing)"
          :aria-label="voiceSupported ? (recognizing ? 'Stop listening' : 'Speak') : 'Not supported'"
          @click="toggleVoice">
          <span v-if="!recognizing">🎙️ Speak</span>
          <span v-else>⏹ Stop</span>
        </button>

        <canvas
          ref="vizCanvas"
          class="viz"
          width="520"
          height="120"
          :class="{ on: recognizing || ttsSpeaking }"
          aria-hidden="true"></canvas>
      </div>
    </div>

    <!-- Live caption -->
    <div class="voice-caption" v-if="recognizing || interimTranscript || finalTranscript" aria-live="polite">
      <div class="cap">
        <span class="pill" :class="{ on: recognizing }">{{ recognizing ? 'Listening…' : 'Idle' }}</span>
        <span class="interim" v-if="interimTranscript && recognizing">{{ interimTranscript }}</span>
        <span class="final" v-if="finalTranscript && !recognizing">{{ finalTranscript }}</span>
      </div>
    </div>

    <!-- Q&A Pathway -->
    <div v-if="hasPathway" class="qa">
      <div class="qa-head">
        <div class="progress">Step {{ currentIndex + 1 }} / {{ pathway.length }}</div>
        <div class="avg">Average: <strong>{{ totalScore }}%</strong> <span v-if="totalScore>=80" class="pass">PASS</span></div>
      </div>

      <!-- Question -->
      <div class="q-text" v-html="currentQuestion?.html || currentQuestion?.q"></div>

      <!-- Rubric quick view (tile grid, no inline styles) -->
      <div v-if="currentQuestion?.rubric?.criteria?.length" class="kpi-grid">
        <div v-for="(c,i) in currentQuestion.rubric.criteria" :key="'crit-'+i" class="crit-tile">
          <div class="kpi-value" style="font-size:13px">{{ c.name }}</div>
          <div class="muted" style="font-size:12px;">{{ c.desc }}</div>
        </div>
      </div>

      <!-- Tips -->
      <ul class="tips" v-if="currentQuestion?.rubric?.tips?.length">
        <li v-for="(t,i) in currentQuestion.rubric.tips" :key="'tip-'+i">💡 {{ t }}</li>
      </ul>

      <!-- Answer -->
      <label>Your Answer
        <textarea
          v-model="userAnswers[currentIndex]"
          rows="6"
          placeholder="Answer here or use the mic. You can also say 'skip question'."
          @keydown.enter.meta.prevent="submitCurrentAnswer()"
          @keydown.enter.ctrl.prevent="submitCurrentAnswer()"
        ></textarea>
      </label>

      <!-- Quick actions -->
      <div class="row">
        <div class="row">
          <button @click="goPrev" :disabled="currentIndex===0 || loading">◀ Prev</button>
          <button class="primary" @click="submitCurrentAnswer()" :disabled="!userAnswers[currentIndex] || loading">Submit for Grade</button>
          <button @click="skipQuestion" :disabled="currentIndex>=pathway.length-1 || loading">Skip ▶</button>
        </div>
        <div class="row">
          <label class="switch">
            <input type="checkbox" v-model="autoMode" />
            <span>Auto Q&amp;A</span>
          </label>
          <button class="secondary" @click="answerNow" :disabled="recognizing">Answer Now</button>
          <button class="ghost" v-if="readyToGradeAll" @click="gradeAll" :disabled="gradeAllWorking">Grade All</button>
        </div>
      </div>

      <!-- Silence / submit warnings -->
      <div class="submit-warning" v-if="submitWarning">
        ⏳ No speech detected — submitting in {{ countdownSecs }}s… keep talking to reset.
      </div>

      <!-- Grade block -->
      <div class="grade" v-if="grades[currentIndex]">
        <h3>
          Score:
          <span :class="{'good': grades[currentIndex].score>=80}">
            {{ grades[currentIndex].score }}
          </span> / 100
          <!-- NEW: compact Delivery Grade badge near current score -->
          <span
            v-if="grades[currentIndex].delivery != null"
            class="delivery-badge mini"
            :class="deliveryClass(grades[currentIndex].delivery)"
            :data-score="grades[currentIndex].delivery"
            :title="'Delivery: ' + grades[currentIndex].delivery + '%'">
            {{ grades[currentIndex].delivery }}%
          </span>
        </h3>

        <div class="cols">
          <div>
            <h4>Strengths</h4>
            <ul class="plain">
              <li v-for="(s,i) in grades[currentIndex].strengths" :key="'st-'+i">✅ {{ s }}</li>
            </ul>
          </div>
          <div>
            <h4>Fixes</h4>
            <ul class="plain">
              <li v-for="(f,i) in grades[currentIndex].fixes" :key="'fx-'+i">🛠️ {{ f }}</li>
            </ul>
          </div>
        </div>

        <div class="percrit" v-if="grades[currentIndex].perCriterion?.length">
          <h4>Per-Criterion</h4>
          <table class="table small">
            <thead><tr><th>Criterion</th><th>Score</th></tr></thead>
            <tbody>
              <tr v-for="(c,i) in grades[currentIndex].perCriterion" :key="'pc-'+i">
                <td>{{ c.name }}</td>
                <td>
                  {{ c.score0to100 }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="recommend" v-if="recForCurrent?.html" v-html="recForCurrent.html"></div>
      </div>

      <!-- Current Attempt Table -->
      <div class="mt">
        <h3>Current Attempt Breakdown</h3>
        <table class="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Question</th>
              <th class="center">Score</th>
              <th class="center">Strengths</th>
              <th class="center">Fixes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(q, i) in pathway" :key="'row-'+i">
              <td>{{ i + 1 }}</td>
              <td class="wrap">{{ previewCell(q) }}</td>
              <td class="center">
                <div class="delivery-wrap">
                  {{ grades[i]?.score ?? '—' }}
                  <!-- NEW: per-row Delivery Grade badge -->
                  <span
                    v-if="grades[i]?.delivery != null"
                    class="delivery-badge mini"
                    :class="deliveryClass(grades[i].delivery)"
                    :data-score="grades[i].delivery"
                    :title="'Delivery: ' + grades[i].delivery + '%'">
                    {{ grades[i].delivery }}%
                  </span>
                </div>
              </td>
              <td class="center">{{ grades[i]?.strengths?.length ?? 0 }}</td>
              <td class="center">{{ grades[i]?.fixes?.length ?? 0 }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2"><strong>Average</strong></td>
              <td class="center">
                <div class="delivery-wrap">
                  <strong>{{ totalScore }}</strong>
                  <!-- Optional average delivery badge if you compute it -->
                  <span
                    v-if="avgDelivery != null"
                    class="delivery-badge mini"
                    :class="deliveryClass(avgDelivery)"
                    :data-score="avgDelivery"
                    :title="'Avg Delivery: ' + avgDelivery + '%'">
                    {{ avgDelivery }}%
                  </span>
                </div>
              </td>
              <td class="center">—</td>
              <td class="center">—</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Suggested Pitch -->
      <div class="mt" v-if="suggestedPitch">
        <h3>Suggested Pitch</h3>
        <div class="answer" v-html="suggestedPitch"></div>
        <div class="row mt">
          <button class="secondary" @click="shareSuggestedPitch">Share</button>
          <button class="ghost" @click="emailSuggestedPitch">Email</button>
          <button class="ghost" @click="downloadSuggestedPitch">Download</button>
          <button class="ghost" @click="generateSuggestedPitch">Refresh</button>
        </div>
      </div>
    </div>

    <!-- If no pathway yet -->
    <div v-else class="muted">
      Build the Q&amp;A pathway to begin. The mic will append your speech to the current answer box.
    </div>

    <!-- Guidance banner (always visible area under right pane) -->
    <div class="coach-banner" :class="{ show: expectingAnswer }">
      <strong>Next step:</strong> Click <span class="pill micpill">🎙️ Start</span> and speak your answer.
      You can also type below. Say <em>“skip question”</em> to move on.
    </div>
  </section>
</main>

<!-- COACH OVERLAY (single instance) -->
<transition name="fade">
  <div v-if="coachOverlayOpen" class="overlay" role="dialog" aria-modal="true" @keydown.esc="closeCoachOverlay">
    <div class="overlay-card glass">
      <header class="overlay-head">
        <div>
          <div class="o-title">Answer Coach</div>
          <div class="o-sub">Match a suggestion while you speak</div>
        </div>
        <button class="ghost sm" @click="closeCoachOverlay" aria-label="Close">✕</button>
      </header>

      <div class="o-meta">
        <div class="o-chip">Step {{ currentIndex + 1 }}/{{ pathway.length }}</div>
        <div class="o-chip">Time left: <strong>{{ overlayCountdown }}s</strong></div>
        <div class="o-chip">Match: <strong :class="{'good': matchPercent>=75, 'ok': matchPercent>=50}">{{ matchPercent }}%</strong></div>
        <div class="o-chip">WPM: <strong>{{ liveWpm }}</strong></div>
        <div class="o-chip">Fillers: <strong>{{ fillerCount }}</strong></div>
      </div>

      <div class="o-question" v-html="currentQuestion?.html || currentQuestion?.q"></div>

      <div class="o-suggestions">
        <label
          v-for="(s, i) in coachSuggestions"
          :key="'sugg-'+i"
          class="sugg"
          :class="{ active: i===selectedSuggestionIdx }"
          @click="selectSuggestion(i)"
        >
          <input type="radio" name="sugg" :checked="i===selectedSuggestionIdx" />
          <div class="sugg-body">
            <div class="sugg-text" v-html="s.html"></div>
            <div class="sugg-keywords">
              <span v-for="k in s.keywords" :key="k" class="kw" :class="{ hit: keywordHits.has(k) }">{{ k }}</span>
            </div>
          </div>
        </label>
      </div>

      <footer class="o-actions">
        <button class="secondary" @click="nextSuggestion">Next Suggestion</button>
        <button class="primary" @click="useSuggestionText">Use & Keep Coaching</button>
        <button class="ghost" @click="lockAndAnswer">Lock & Start Answering</button>
      </footer>
    </div>
  </div>
</transition>

<!-- Attempts History -->
<section class="pane glass">
  <h2 class="section-title">Attempt History</h2>
  <table class="table" v-if="attempts.length">
    <thead>
      <tr>
        <th>#</th>
        <th>Date</th>
        <th>Style</th>
        <th class="center">Average</th>
        <th>Details</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(a, i) in attempts" :key="'att-'+a.ts">
        <td>{{ i + 1 }}</td>
        <td>{{ new Date(a.ts).toLocaleString() }}</td>
        <td>{{ a.style }}</td>
        <td class="center">{{ a.totalScore }}%</td>
        <td>
          <details>
            <summary>Per Question</summary>
            <ul class="plain">
              <li v-for="(g, gi) in a.perQ" :key="'g-'+gi">Q{{ gi + 1 }}: {{ g.score }}/100</li>
            </ul>
          </details>
        </td>
      </tr>
    </tbody>
  </table>
  <div v-else class="muted">No attempts yet.</div>
</section>

<footer class="foot glass">
  <div v-if="loading">Working with AI…</div>
  <div v-else>&nbsp;</div>
</footer>
<br /><br />
  </div>
</template>


<script>
export default {
  name: 'MovieAnalystAI',
  data() {
    return {
gptApiKey: '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',
      googleApiKey: 'AIzaSyBboVjZMYbpCulM_NhAuW5I_e6ZwD5f8qA',
      searchEngineId: '87a5cd3086b3e4db7',
      cseCx: '87a5cd3086b3e4db7',
      // App state
      loading: false,
      answer: '',

suggestedPitch: '',
suggestedPitchUpdatedAt: 0,

      scriptTitle: '',
      scriptAuthor: '',
      scriptGenre: '',
      scriptPages: 10,
      scriptText: '',
      genres: ['Drama','Comedy','Thriller','Action','Horror','Sci-Fi','Fantasy','Romance','Animation','Documentary'],

      // Pathway / Simulation
      pitchStyle: 'Elevator',
      pathway: [],
      currentIndex: -1,
      userAnswers: [],
      grades: [],
      recommendations: [],
      attempts: [],

      // Saves
      savedPitches: [],
      currentPitch: null,
      openLoadDialog: false,
      coachOverlayOpen: false,
coachSuggestions: [],          // [{html, plain, keywords:string[]}]
selectedSuggestionIdx: 0,

overlayDurationSecs: 45,
overlayCountdown: 45,
overlayTimerId: null,
overlayTickId: null,

keywordHits: new Set(),
matchPercent: 0,
liveWpm: 0,
fillerCount: 0,
speechStartAt: 0,
spokenWordCount: 0,


      // Voice / TTS / Viz
      voiceSupported: false,
      recognition: null,
      recognizing: false,
      interimTranscript: '',
      finalTranscript: '',
      ttsSpeaking: false,
      selectedVoice: null,
      ttsRate: 1.0,
      ttsPitch: 1.05,
      viz: { ctx: null, dpr: (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1, raf: null },

      // Auto flow (silence → grade → next)
      silenceMs: 8000,
      silenceTimer: null,
      lastSpeechAt: 0,
      autoAdvance: true,
      expectingAnswer: false,
autoMode: false,          // was autoAdvance; default OFF so it won't skip
gradeAtEnd: true,         // grade only when all answers are done
gradeAllWorking: false,

// silence/submit timers
warnAtMs: 3000,
submitAtMs: 5000,
warnTimer: null,
submitTimer: null,
countdownInterval: null,
submitWarning: false,
countdownSecs: 0,
hasSpoken: false,

    };
  },
  computed: {
    hasPathway() {
      return Array.isArray(this.pathway) && this.pathway.length > 0 && this.currentIndex >= 0;
    },
    currentQuestion() {
      return this.hasPathway ? this.pathway[this.currentIndex] : null;
    },
    totalScore() {
      if (!this.grades.length) return 0;
      const s = this.grades.reduce((a, g) => a + (g?.score || 0), 0) / Math.max(1, this.grades.length);
      return Math.round(s);
    },
    recForCurrent() {
      return this.recommendations.find(r => r.idx === this.currentIndex) || null;
    },
    allAnswered() {
  return this.hasPathway && this.userAnswers.every(a => (a || '').trim().length > 0);
},
readyToGradeAll() {
  // show button once all questions have some answer and not graded yet
  return this.gradeAtEnd && this.allAnswered && (!this.grades.length || this.grades.length < this.pathway.length);
},
stepColorClass() {
  const palette = [
    'mic-c-blue',
    'mic-c-teal',
    'mic-c-pink',
    'mic-c-amber',
    'mic-c-lime',
    'mic-c-purple'
  ];
  if (!this.hasPathway || this.currentIndex < 0) return 'mic-c-default';
  return palette[this.currentIndex % palette.length];
},
    allowedSecs() {
  const map = {
    'Elevator': 40, 'Producer Room': 60, 'Festival Lab': 75,
    'Writer-Director Deck': 90, 'Investor (ROI)': 60, 'Studio (Commercial)': 45
  };
  return map[this.pitchStyle] || 60;
},


  },
  mounted() {
    this.initTTSVoices(); 
    // key restore
try {
  const savedKey = localStorage.getItem('movieAnalyst_openai_key');
  if (savedKey) this.gptApiKey = savedKey;
} catch (e) { console.warn('key restore failed', e); }
    this.loadPitches();
    this.setupSpeech();
    this.initViz();
  },
  beforeDestroy() {
    if (this.viz?.raf) cancelAnimationFrame(this.viz.raf);
    this.clearSilenceTimer();
  },
  methods: {
    /* ======= Utilities ======= */
    persistKey() {
try { localStorage.setItem('movieAnalyst_openai_key', this.gptApiKey || ''); }
catch (e) { console.warn('persistKey failed', e); }

    },
    vueSet(arr, idx, val) {
      if (this.$set) this.$set(arr, idx, val); else arr.splice(idx, 1, val);
    },
    safeParseJSON(s) {
      try {
        const a = s.indexOf('['), b = s.lastIndexOf(']');
        if (a >= 0 && b > a) return JSON.parse(s.slice(a, b + 1));
        const i = s.indexOf('{'), j = s.lastIndexOf('}');
        if (i >= 0 && j > i) return JSON.parse(s.slice(i, j + 1));
        return {};
      } catch (_) {
        return {};
      }
    },
    stripHtml(x = '') {
      const d = document.createElement('div');
      d.innerHTML = String(x);
      return (d.textContent || d.innerText || '').trim();
    },
    previewCell(q) {
      const raw = (q && (q.q || q.html)) || '';
      return this.stripHtml(raw).slice(0, 140);
    },
    initTTSVoices() {
  if (!('speechSynthesis' in window)) return;
  const loadVoices = () => {
    const voices = window.speechSynthesis.getVoices() || [];
    const v = this.pickVoiceFrom(voices);
    if (v) this.selectedVoice = v;
  };
  // Some browsers populate voices async:
  window.speechSynthesis.onvoiceschanged = loadVoices;
  loadVoices();
},

pickVoiceFrom(voices) {
  if (!voices || !voices.length) return null;

  // Prefer common female UK voices across Chrome/Edge/Windows/macOS
  const preferredByName = [
    'Google UK English Female',
    'Microsoft Sonia Online (Natural) - English (United Kingdom)',
    'Microsoft Libby Online (Natural) - English (United Kingdom)',
    'Microsoft Hazel Desktop - English (Great Britain)',
    'Serena',   // macOS UK Female
    'Kate'      // older macOS UK Female
  ];
  let v = voices.find(v => preferredByName.includes(v.name));
  if (v) return v;

  // Otherwise: any en-GB; prefer ones whose name suggests “Female”
  const enGB = voices.filter(v => /^en-GB/i.test(v.lang));
  v = enGB.find(v => /female/i.test(v.name)) || enGB[0];
  if (v) return v;

  // Last resort: any English voice that hints at UK/female
  const en = voices.filter(v => /^en-/i.test(v.lang));
  return en.find(v => /female|UK|GB|British/i.test(v.name)) || en[0] || null;
},
async prepareCoachOverlay(qtext) {
  // 1) Generate 2–3 suggestions (AI if key exists, else fallback)
  const tips = (this.currentQuestion?.rubric?.tips || []).slice(0, 3);
  let htmlList = [];

  if (this.gptApiKey && String(this.gptApiKey).startsWith('sk-')) {
    const sys = 'You are a senior pitch coach. Create 3 concise, speakable suggestion snippets (1–2 sentences each). Keep vivid, specific nouns/verbs and include 4–7 distinctive keywords per snippet.';
    const user =
      `STYLE: ${this.pitchStyle}\nGENRE: ${this.scriptGenre}\nTITLE: ${this.scriptTitle}\nQUESTION: ${qtext}\n` +
      `TIPS: ${tips.join(' | ') || '—'}\n` +
      `Return JSON [{"text": "..."} x3].`;
    const raw = await this.askAI([{role:'system',content:sys},{role:'user',content:user}],'gpt-4o',0.4);
    const j = this.safeParseJSON(raw);
    if (Array.isArray(j) && j.length) htmlList = j.map(x => x.text).filter(Boolean).slice(0,3);
  }

  if (!htmlList.length) {
    htmlList = [
      `Lead with a crisp logline: <em>[Hero] must [do X] to [win Y], or else [stakes].</em> Name the world and the twist.`,
      `State conflict and consequences: <em>When [inciting event], they risk [loss]; only by [plan] can they win.</em>`,
      `Market & comps: <em>Fans of [Comp A] and [Comp B]</em>—but with a fresh angle in [hook], feasible at [budget band].`
    ];
  }

  this.coachSuggestions = htmlList.map(s => {
    const plain = this.stripHtml(s);
    const keywords = this.extractKeywords(plain);
    return { html: s, plain, keywords };
  });

  this.selectedSuggestionIdx = 0;

  // 2) Start timer
  this.overlayDurationSecs = this.allowedSecs;
  this.overlayCountdown = this.overlayDurationSecs;
  if (this.overlayTimerId) clearInterval(this.overlayTimerId);
  if (this.overlayTickId) clearInterval(this.overlayTickId);

  const t0 = Date.now();
  this.speechStartAt = t0;

  this.overlayTickId = setInterval(() => {
    // update WPM live from interim/final
    const totalWords = (this.interimTranscript + ' ' + this.finalTranscript)
      .trim().split(/\s+/).filter(Boolean).length;
    this.spokenWordCount = totalWords;
    const mins = Math.max(1/60, (Date.now() - this.speechStartAt) / 60000);
    this.liveWpm = Math.round(totalWords / mins);
    this.updateMatchScore();
  }, 400);

  this.overlayTimerId = setInterval(() => {
    this.overlayCountdown = Math.max(0, this.overlayDurationSecs - Math.round((Date.now() - t0)/4000));
    if (this.overlayCountdown <= 0) {
      clearInterval(this.overlayTimerId);
      this.overlayTimerId = null;
      // Auto lock on time up
      this.lockAndAnswer();
    }
  }, 500);

  this.coachOverlayOpen = true;
},

closeCoachOverlay() {
  this.coachOverlayOpen = false;
  if (this.overlayTimerId) { clearInterval(this.overlayTimerId); this.overlayTimerId = null; }
  if (this.overlayTickId) { clearInterval(this.overlayTickId); this.overlayTickId = null; }
},

selectSuggestion(i) {
  this.selectedSuggestionIdx = i;
  this.keywordHits.clear();
  this.updateMatchScore(); // recompute immediately
},

nextSuggestion() {
  this.selectSuggestion( (this.selectedSuggestionIdx + 1) % this.coachSuggestions.length );
},

useSuggestionText() {
  const s = this.coachSuggestions[this.selectedSuggestionIdx]?.plain || '';
  const cur = (this.userAnswers[this.currentIndex] || '').trim();
  this.userAnswers[this.currentIndex] = cur ? (cur + ' ' + s) : s;
},

lockAndAnswer() {
  // Close overlay but keep mic live; user continues with their answer box.
  this.closeCoachOverlay();
},

/* === Matching / Metrics === */
extractKeywords(text='') {
  const stop = new Set(('a,an,the,of,and,or,but,to,in,on,for,with,as,by,at,from,that,which,who,whom,into,about,over,under,' +
    'is,are,was,were,be,been,being,have,has,had,do,does,did,not,will,would,should,could,can,may,might,if,then,else,' +
    'this,that,these,those,up,down,out,so,than,too,very,just,really,like').split(','));
  const words = text.toLowerCase().replace(/[^a-z0-9\s-]/g,' ').split(/\s+/).filter(w => w && !stop.has(w) && w.length > 2);
  // get top distinct content words
  const freq = {};
  words.forEach(w => freq[w]=(freq[w]||0)+1);
  return Object.keys(freq).sort((a,b)=>freq[b]-freq[a]).slice(0,7);
},

updateMatchScore() {
  const s = this.coachSuggestions[this.selectedSuggestionIdx];
  if (!s) return;

  const spoken = (this.finalTranscript + ' ' + this.interimTranscript).toLowerCase();
  const tokens = new Set(spoken.replace(/[^a-z0-9\s-]/g,' ').split(/\s+/).filter(Boolean));
  this.keywordHits.clear();
  let hits = 0;
  s.keywords.forEach(k => {
    if (tokens.has(k.toLowerCase())) { this.keywordHits.add(k); hits++; }
  });

  const jaccard = this.jaccardSim(
    new Set(s.plain.toLowerCase().split(/\s+/).filter(Boolean)),
    tokens
  );

  const fillerRx = /\b(um+|uh+|like|you know|sort of|kind of|basically|literally|so\b|i mean)\b/gi;
  const fillers = (spoken.match(fillerRx) || []).length;
  this.fillerCount = fillers;

  // Weighted score: 70% keyword coverage + 20% jaccard + 10% pacing bonus (120–160 wpm sweet spot)
  const kwPct = s.keywords.length ? (hits / s.keywords.length) : 0;
  const pace = this.liveWpm;
  const paceBonus = (pace >= 110 && pace <= 170) ? 1 : Math.max(0, 1 - Math.abs(pace - 140) / 140);
  let score = 0.7*kwPct + 0.2*jaccard + 0.1*paceBonus;

  // Penalize many fillers
  if (fillers >= 5) score *= 0.85;
  if (fillers >= 10) score *= 0.7;

  this.matchPercent = Math.round(score * 100);
},

jaccardSim(aSet, bSet) {
  let inter = 0;
  aSet.forEach(w => { if (bSet.has(w)) inter++; });
  const union = new Set([...aSet, ...bSet]).size || 1;
  return inter / union;
},

async generateSuggestedPitch() {
  // Build a compact context from current answers & grades
  const answered = this.pathway.map((q, i) => ({
    q: this.stripHtml(q.html || q.q || `Q${i+1}`),
    a: (this.userAnswers[i] || '').trim(),
    g: this.grades[i] || null
  })).filter(x => x.a);

  const topFixes = [];
  const topWins = [];
  for (const g of this.grades) {
    if (!g) continue;
    (g.fixes || []).slice(0, 2).forEach(f => { if (topFixes.length < 6) topFixes.push(f); });
    (g.strengths || []).slice(0, 2).forEach(s => { if (topWins.length < 6) topWins.push(s); });
  }

  const sys = `You are a senior film producer. Compose a sharp, 60–120 second pitch
that would be spoken aloud. Tone should match the selected pitch style.
Keep it practical, specific, and commercially aware.`;
  const user =
`TITLE: ${this.scriptTitle || '(Untitled)'}
GENRE: ${this.scriptGenre || '—'}
STYLE: ${this.pitchStyle}
EXCERPT (first 1k chars):
${(this.scriptText || '').slice(0, 1000)}

ANSWERED Q&A (trimmed):
${answered.map(x => `- ${x.q}\n  → ${x.a}`).join('\n')}

STRENGTHS (auto): ${topWins.join(' | ') || '—'}
FIXES TO ADDRESS (auto): ${topFixes.join(' | ') || '—'}

Return clean HTML only with:
<h4>Title — Style</h4>
<p><em>One-sentence logline</em></p>
<ul>
  <li>Hook/World:</li>
  <li>Protagonist & Goal:</li>
  <li>Conflict & Stakes:</li>
  <li>Audience & Comps:</li>
  <li>Feasibility/Budget note:</li>
</ul>
<p><strong>Ask / Next Steps:</strong> …</p>`;

  let html = '';
  try {
    html = await this.askAI(
      [{ role: 'system', content: sys }, { role: 'user', content: user }],
      'gpt-4o',
      0.25
    );
  } catch (e) {
    console.warn('generateSuggestedPitch failed', e);
  }

  // Fallback if no model response
  if (!html || !/<h4|<p|<ul/i.test(html)) {
    const logline = (answered.find(x => /logline/i.test(x.q))?.a || '').trim();
    html = `
      <h4>${this.scriptTitle || '(Untitled)'} — ${this.pitchStyle}</h4>
      <p><em>${logline || 'A concise one-sentence premise goes here.'}</em></p>
      <ul>
        <li><strong>Hook/World:</strong> ${answered[0]?.a?.slice(0,180) || 'Define the world and the spark.'}</li>
        <li><strong>Protagonist & Goal:</strong> ${answered[1]?.a?.slice(0,180) || 'Who they are and what they want.'}</li>
        <li><strong>Conflict & Stakes:</strong> ${answered[2]?.a?.slice(0,180) || 'What stands in the way, what happens if they fail.'}</li>
        <li><strong>Audience & Comps:</strong> ${answered[4]?.a?.slice(0,180) || 'Name 2–3 relevant comps and audience.'}</li>
        <li><strong>Feasibility/Budget:</strong> ${answered[5]?.a?.slice(0,180) || 'A credible budget band and plan note.'}</li>
      </ul>
      <p><strong>Ask / Next Steps:</strong> Looking for feedback, partners, or financing to package and take to market.</p>
    `;
  }

  this.suggestedPitch = html;
  this.suggestedPitchUpdatedAt = Date.now();
},

plainText(x='') {
  return this.stripHtml(x).replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
},
async shareSuggestedPitch() {
  const text = this.plainText(this.suggestedPitch || '');
  const title = `${this.scriptTitle || 'Pitch'} — ${this.pitchStyle}`;
  if (navigator.share) {
    try { await navigator.share({ title, text }); }
    catch (e) { console.warn('Web Share failed', e); await this.copyToClipboard(text); alert('Copied pitch to clipboard.'); }
  } else {
    await this.copyToClipboard(text);
    alert('Copied pitch to clipboard.');
  }
},
emailSuggestedPitch() {
  const subject = `${this.scriptTitle || 'Pitch'} — ${this.pitchStyle}`;
  const body = encodeURIComponent(this.plainText(this.suggestedPitch || ''));
  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
},
downloadSuggestedPitch() {
  const txt = `# ${this.scriptTitle || 'Pitch'} — ${this.pitchStyle}\n\n${this.plainText(this.suggestedPitch || '')}\n`;
  const blob = new Blob([txt], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${(this.scriptTitle || 'pitch').replace(/[^\w-]+/g,'_')}.md`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
},
copyToClipboard(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text).catch(e => console.warn('clipboard failed', e));
  const ta = document.createElement('textarea');
  ta.value = text; document.body.appendChild(ta); ta.select();
  try { document.execCommand('copy'); } catch (e) { console.warn('execCommand copy failed', e); }
  document.body.removeChild(ta);
},

    /* ======= File & context ======= */
    async onScriptUpload(file) {
      if (!file) return;
      try {
        const text = await file.text();
        this.scriptText = (this.scriptText ? this.scriptText + "\n" : "") + text.slice(0, 20000);
        if (!this.scriptTitle) this.scriptTitle = file.name.replace(/\.[^.]+$/, '');
      } catch (e) {
        console.error('Upload failed', e);
      }
    },
    async analyzeScriptContext() {
      if (!this.scriptText.trim()) {
        this.answer = 'Paste or upload a script excerpt first.';
        return;
      }
      const sys = 'You are a movie producer at a production company. You evaluate material for market fit, clarity, and feasibility.';
      const user =
        `TITLE: ${this.scriptTitle}\n` +
        `GENRE: ${this.scriptGenre}\n` +
        `PAGES: ${this.scriptPages}\n` +
        `AUTHOR: ${this.scriptAuthor}\n\n` +
        `EXCERPT:\n${this.scriptText.slice(0, 6000)}\n\n` +
        `Give a concise diagnostic: logline (1 sentence), strengths (3 bullets), production concerns (3 bullets), and audience comps (2-3 recent).`;

      const html = await this.askAI(
        [{ role: 'system', content: sys }, { role: 'user', content: user }],
        'gpt-4o'
      );
      this.answer = html || 'No analysis returned.';
    },

    /* ======= OpenAI (frontend) ======= */
    async askAI(messages, model = 'gpt-4o', temperature = 0.25) {
      if (!this.gptApiKey || !String(this.gptApiKey).startsWith('sk-')) return '';
      try {
        this.loading = true;
        const res = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.gptApiKey}`
          },
          body: JSON.stringify({ model, messages, temperature })
        });
        if (!res.ok) {
          console.error('OpenAI error', await res.text());
          return '';
        }
        const data = await res.json();
        return data?.choices?.[0]?.message?.content || '';
      } catch (e) {
        console.error('AI error', e);
        return '';
      } finally {
        this.loading = false;
      }
    },

    /* ======= Pathway ======= */
    async startPathway() {
      const sys = 'You are a movie analyst and pitch coach inside a production company. Think like a producer: market, audience, comps, feasibility, IP risk, and budget realism.';
      const ctx =
        `CONTEXT\nTitle: ${this.scriptTitle}\n` +
        `Genre: ${this.scriptGenre}\n` +
        `Pages: ${this.scriptPages}\n` +
        `Style: ${this.pitchStyle}\n` +
        `Excerpt (first 4-6k chars):\n${this.scriptText.slice(0, 6000)}`;
      const user = 'Create a JSON pathway of 6–8 questions tailored to this project. Each item must have: id, html (or q), rubric { criteria:[{name, desc, weight 0-1}], tips:[string] }. Focus on clarity of premise, conflict/stakes, audience & comps, team/why now, feasibility/budget, and unique hook. Keep criteria measurable.';

      let list = [];
      const raw = await this.askAI(
        [{ role: 'system', content: sys }, { role: 'user', content: ctx }, { role: 'user', content: user }],
        'gpt-4o'
      );
      if (raw) {
        const json = this.safeParseJSON(raw);
        list = Array.isArray(json) ? json : (json?.pathway || []);
      }
      if (!list.length) list = this._fallbackPath();

      this.pathway = list.map((x, i) => ({
        id: x.id || i + 1,
        q: x.q || x.question || undefined,
        html: x.html || (x.q ? `<p>${x.q}</p>` : undefined),
        rubric: this._normalizeRubric(x.rubric)
      })).slice(0, 8);

      this.currentIndex = 0;
      this.userAnswers = Array(this.pathway.length).fill('');
      this.grades = [];
      this.recommendations = [];
      this.answer = 'Pathway ready. I’ll read the first question aloud—then start speaking. Say “skip question” to move on.';
      // Speak & listen
      this.askCurrentQuestion();
    },
    _normalizeRubric(r) {
      const rb = r || {};
      let crit = Array.isArray(rb.criteria) ? rb.criteria : [];
      crit = crit.map(c => ({
        name: c.name || 'Clarity',
        desc: c.desc || c.description || 'Clarity and specificity',
        weight: Math.max(0, Math.min(1, Number(c.weight || 0.2)))
      }));
      const total = crit.reduce((a, c) => a + (c.weight || 0), 0) || 1;
      crit = crit.map(c => ({ ...c, weight: (c.weight || 0) / total }));
      return { criteria: crit, tips: Array.isArray(rb.tips) ? rb.tips : [] };
    },
    _fallbackPath() {
      return [
        { id: 1, html:'<p><strong>Logline:</strong> One sentence (≤20 words). Who + Goal + Obstacle + Stakes.</p>', rubric: { criteria: [{ name: 'Clarity', desc: 'One-sentence premise', weight: .4 }, { name: 'Hook', desc: 'Unique selling point', weight: .3 }, { name: 'Tone/Genre fit', desc: 'Tone matches genre', weight: .3 }], tips: ['Start with protagonist + goal + obstacle.'] } },
        { id: 2, html:'<p><strong>Protagonist & Goal:</strong> Who are they and what do they want? Be specific.</p>', rubric: { criteria: [{ name: 'Goal', desc: 'Clear desire', weight: .4 }, { name: 'Empathy', desc: 'Audience connection', weight: .3 }, { name: 'Conflict tie', desc: 'Goal linked to central conflict', weight: .3 }], tips: ['Use active verbs; avoid vague traits.'] } },
        { id: 3, html:'<p><strong>Conflict & Stakes:</strong> What stands in the way? What happens if they fail?</p>', rubric: { criteria: [{ name: 'Conflict', desc: 'Specific external/internal', weight: .4 }, { name: 'Stakes', desc: 'Meaningful consequences', weight: .4 }, { name: 'Urgency', desc: 'Time/pressure', weight: .2 }], tips: ['Explain “what happens if they fail”.'] } },
        { id: 4, html:'<p><strong>Why Now & Why You:</strong> Timeliness and authorship.</p>', rubric: { criteria: [{ name: 'Timeliness', desc: 'Topical/market fit', weight: .4 }, { name: 'Authorship', desc: 'Why you can tell it', weight: .3 }, { name: 'Credibility', desc: 'Proof of capability', weight: .3 }], tips: ['Reference comps, awards, or access.'] } },
        { id: 5, html:'<p><strong>Comps & Audience:</strong> Name 2–3 comps (≤5 yrs) and target audience.</p>', rubric: { criteria: [{ name: 'Comps', desc: 'Relevant, recent comps', weight: .4 }, { name: 'Audience', desc: 'Who will watch', weight: .4 }, { name: 'Positioning', desc: 'How it stands out', weight: .2 }], tips: ['Use 2–3 comps in 5yrs.'] } },
        { id: 6, html:'<p><strong>Feasibility/Budget:</strong> Production plan, range, constraints, and risks.</p>', rubric: { criteria: [{ name: 'Feasibility', desc: 'Budget aligns with story', weight: .5 }, { name: 'Plan', desc: 'Schedule/locations/attachments', weight: .3 }, { name: 'Risks', desc: 'Foresee issues/mitigation', weight: .2 }], tips: ['Give a range & phasing.'] } }
      ];
    },

    /* ======= Ask / Auto-advance ======= */
async askCurrentQuestion() {
  const qtext = this.stripHtml(this.currentQuestion?.html || this.currentQuestion?.q || '');
  this.stopMic();                       // don't capture TTS
  await this.speak(qtext);

  // Build suggestions & open coach overlay
  await this.prepareCoachOverlay(qtext);

  // Reset live metrics
  this.finalTranscript = '';
  this.interimTranscript = '';
  this.hasSpoken = false;
  this.spokenWordCount = 0;
  this.fillerCount = 0;
  this.liveWpm = 0;
  this.keywordHits.clear();
  this.matchPercent = 0;

  // Start listening
  this.startMic();
  this.expectingAnswer = true;
  this.lastSpeechAt = Date.now();
  this.scheduleSilenceWatch();
},


answerNow() {
  this.stopMic(); // ensure clean start
  this.finalTranscript = '';
  this.interimTranscript = '';
  this.hasSpoken = false;
  this.startMic();
  this.lastSpeechAt = Date.now();
  this.scheduleSilenceWatch();   // will only auto-submit if user actually speaks
},
scheduleSilenceWatch() {
  // Recompute remaining delays each time we get speech
  this.clearSilenceTimers();

  // Don’t auto-submit unless we’re actually listening and the user has spoken at least once
  if (!this.recognizing || !this.autoMode) return;

  const elapsed = Date.now() - this.lastSpeechAt;
  const warnDelay = Math.max(0, this.warnAtMs - elapsed);
  const submitDelay = Math.max(0, this.submitAtMs - elapsed);

  // 3s warning
  this.warnTimer = setTimeout(() => {
    if (!this.hasSpoken) return; // if nothing was said yet, don’t warn
    this.submitWarning = true;
    // Live countdown to submit (submitAt - now)
    const left = Math.max(0, this.submitAtMs - (Date.now() - this.lastSpeechAt));
    this.countdownSecs = Math.ceil(left / 1000);
    this.countdownInterval = setInterval(() => {
      const remain = Math.max(0, this.submitAtMs - (Date.now() - this.lastSpeechAt));
      this.countdownSecs = Math.ceil(remain / 1000);
      if (remain <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }, 250);
  }, warnDelay);

  // 5s auto-submit
  this.submitTimer = setTimeout(async () => {
    this.submitWarning = false;
    if (!this.hasSpoken) {
      // If the user truly said nothing, don’t skip in manual mode.
      if (this.autoMode) this.skipQuestion();
      return;
    }
    this.stopMic();
    await this.submitCurrentAnswer(true); // autoNext = true
  }, submitDelay);
},
clearSilenceTimers() {
  if (this.warnTimer) { clearTimeout(this.warnTimer); this.warnTimer = null; }
  if (this.submitTimer) { clearTimeout(this.submitTimer); this.submitTimer = null; }
  if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
  this.submitWarning = false;
  this.countdownSecs = 0;
},


    /* ======= Q&A / grading ======= */
async submitCurrentAnswer(autoNext = false) {
  if (!this.currentQuestion) return;
  const a = (this.userAnswers[this.currentIndex] || '').trim();
  if (!a) return;

  this.clearSilenceTimers();
  this.expectingAnswer = false;

  if (this.gradeAtEnd) {
    // Just move on; grading happens when all answers are collected
    if (this.currentIndex < this.pathway.length - 1) {
      this.currentIndex++;
      if (this.autoMode || autoNext) this.askCurrentQuestion();
    } else {
      // All answered — grade now
      await this.gradeAll();
      this.stopMic();
    }
    return;
  }

  // ---- (legacy per-question grading path; keep if you want both modes) ----
  const rubric = this.currentQuestion.rubric;
  let scored = null;

  const sys = 'You are a strict but helpful producer grading answers to improve a film pitch. Score against the rubric only.';
  const user =
    `PROJECT: ${this.scriptTitle} (${this.scriptGenre})\n` +
    `EXCERPT:\n${this.scriptText.slice(0, 4000)}\n\n` +
    `QUESTION: ${this.stripHtml(this.currentQuestion.html || this.currentQuestion.q || '')}\n` +
    `RUBRIC:${JSON.stringify(rubric)}\n\n` +
    `ANSWER:${a}\n\n` +
    `Return JSON { "score":0-100, "strengths":[...], "fixes":[...], "perCriterion":[{"name","score0to100"}] }`;

  const raw = await this.askAI(
    [{ role: 'system', content: sys }, { role: 'user', content: user }],
    'gpt-4o',
    0.2
  );
  if (raw) {
    const g = this.safeParseJSON(raw);
    scored = {
      score: Math.max(0, Math.min(100, Number(g?.score || 0))),
      strengths: Array.isArray(g?.strengths) ? g.strengths : [],
      fixes: Array.isArray(g?.fixes) ? g.fixes : [],
      perCriterion: Array.isArray(g?.perCriterion) ? g.perCriterion : []
    };
  }
  if (!scored) scored = this.gradeLocally(a, rubric);

  this.vueSet(this.grades, this.currentIndex, scored);

  // Optional recs + pitch suggestion
  let recHtml = this.localRecommendations(scored, rubric, this.pitchStyle);
  if (this.gptApiKey) {
    const recSys = 'You are a movie pitch improvement assistant.';
    const recUser = `Based on the rubric and score ${scored.score}, give 2–3 concrete rewrites for a ${this.pitchStyle} pitch.`;
    const ai = await this.askAI([{ role: 'system', content: recSys }, { role: 'user', content: recUser }], 'gpt-4o', 0.25);
    if (ai) recHtml = ai;
  }
  const existing = this.recommendations.find(r => r.idx === this.currentIndex);
  if (existing) existing.html = recHtml;
  else this.recommendations.push({ idx: this.currentIndex, html: recHtml });

  await this.generateSuggestedPitch();

 if (this.currentIndex < this.pathway.length - 1) {
    this.currentIndex++;
    if (autoNext) {
      this.askCurrentQuestion();            // will set expectingAnswer=true again
    } else {
      this.startMic(); this.lastSpeechAt = Date.now(); this.scheduleSilenceWatch();
    }
  } else {
    this.archiveAttemptIfComplete();
    this.stopMic();                         // expectingAnswer=false
  }
},
    skipQuestion() {
      if (!this.hasPathway) return;
      if (this.currentIndex < this.pathway.length - 1) {
        this.userAnswers[this.currentIndex] = '';
        this.currentIndex++;
        // Ask next immediately
        this.askCurrentQuestion();
      } else {
        this.stopMic();
      }
    },
    goNext() {
      if (this.hasPathway && this.currentIndex < this.pathway.length - 1) {
        this.currentIndex++;
        this.askCurrentQuestion();
      }
    },
    goPrev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.askCurrentQuestion();
      }
    },
    async gradeAll() {
  if (!this.hasPathway) return;
  this.gradeAllWorking = true;

  // Build batch payload
  const batch = this.pathway.map((q, i) => ({
    idx: i,
    question: this.stripHtml(q.html || q.q || `Q${i+1}`),
    rubric: q.rubric || { criteria: [] },
    answer: (this.userAnswers[i] || '').trim()
  }));

  // Ask AI for all grades at once
  let graded = null;
  try {
    const sys = 'You are a strict but helpful producer. Grade each answer only against its rubric.';
    const user =
      `Return a JSON array (same order) where each item is:\n` +
      `{"score":0-100,"strengths":[...],"fixes":[...],"perCriterion":[{"name","score0to100"}]}\n\n` +
      `BATCH:\n` + JSON.stringify(batch);
    const raw = await this.askAI([{ role: 'system', content: sys }, { role: 'user', content: user }], 'gpt-4o', 0.2);
    const j = this.safeParseJSON(raw);
    if (Array.isArray(j) && j.length === batch.length) graded = j;
  } catch (e) {
    console.warn('gradeAll AI failed', e);
  }

  if (!graded) {
    // Local fallback per item
    graded = batch.map(b => this.gradeLocally(b.answer, b.rubric));
  }

  this.grades = graded.map(g => ({
    score: Math.max(0, Math.min(100, Number(g?.score || 0))),
    strengths: Array.isArray(g?.strengths) ? g.strengths : [],
    fixes: Array.isArray(g?.fixes) ? g.fixes : [],
    perCriterion: Array.isArray(g?.perCriterion) ? g.perCriterion : []
  }));

  // Save attempt
  this.archiveAttemptIfComplete();

  // Build/update recommendations per question (local quick version)
  this.recommendations = this.grades.map((g, i) => ({
    idx: i,
    html: this.localRecommendations(g, this.pathway[i]?.rubric || {}, this.pitchStyle)
  }));

  // Build/update suggested pitch
  await this.generateSuggestedPitch();

  this.gradeAllWorking = false;
},
    gradeLocally(answer, rubric) {
      const text = answer.toLowerCase();
      const len = answer.split(/\s+/).filter(Boolean).length;
      const hasNumbers = /\b\d/.test(answer);
      const hasWho = /\b(protagonist|hero|she|he|they|lead|character|teen|mother|detective|writer|director)\b/.test(text);
      const hasGoal = /\bgoal|wants?|needs?|must|tries|seeks|to\s+\w+/.test(text);
      const hasConflict = /\bconflict|vs\.|versus|antagonist|but|however|until|obstacle|risk|threat|villain/.test(text);
      const hasStakes = /\bif\s+not|or\s+else|stakes|lose|death|ruin|destroy|fail|failure|cost|price/.test(text);
      const hasComps = /\bcomp|like|fans\s+of|meets|similar\s+to/.test(text);
      const hasAudience = /\baudienc|four-quadrant|demo|viewers|teens|adults|famil(y|ies)/.test(text);
      const hasPlan = /\bbudget|schedule|locations?|shoot|days|weeks|crew|attachment|cast|post|vfx|sfx/.test(text);

      const crit = rubric.criteria || [];
      const perCriterion = crit.map(c => {
        let base = 50;
        if (/clarity|logline|premise/i.test(c.name)) base += hasWho && hasGoal ? 25 : 0;
        if (/hook|unique|original/i.test(c.name)) base += /unique|fresh|twist|high[- ]concept/.test(text) ? 20 : 0;
        if (/tone|genre/i.test(c.name)) base += /drama|comedy|thriller|horror|sci[- ]?fi|romance|action|fantasy/.test(text) ? 10 : 0;
        if (/goal|protagonist/i.test(c.name)) base += (hasWho ? 10 : 0) + (hasGoal ? 10 : 0);
        if (/conflict/i.test(c.name)) base += hasConflict ? 20 : 0;
        if (/stakes|urgency/i.test(c.name)) base += hasStakes ? 20 : 0;
        if (/comps|audience/i.test(c.name)) base += (hasComps ? 10 : 0) + (hasAudience ? 10 : 0);
        if (/feasib|budget|plan|risk/i.test(c.name)) base += (hasPlan ? 15 : 0) + (hasNumbers ? 5 : 0);
        if (len < 15) base -= 15;
        return { name: c.name, score0to100: Math.max(10, Math.min(100, Math.round(base))) };
      });

      const score = Math.round(perCriterion.reduce((sum, pc, i) => {
        const w = crit[i]?.weight ?? (1 / Math.max(1, crit.length));
        return sum + pc.score0to100 * w * 1.0;
      }, 0));

      const strengths = [];
      if (hasWho && hasGoal) strengths.push('Clear protagonist and goal.');
      if (hasConflict) strengths.push('Conflict identified.');
      if (hasStakes) strengths.push('Stakes articulated.');
      if (hasComps || hasAudience) strengths.push('Market positioning mentioned.');
      if (hasPlan) strengths.push('Touches feasibility/plan.');

      const fixes = [];
      if (!/\b(unique|fresh|twist|what if|high[- ]concept|never\s+seen|reinvents|subverts)\b/.test(text)) fixes.push('State the unique hook in one crisp clause.');
      if (!hasStakes) fixes.push('Make consequences explicit if the goal fails.');
      if (!hasComps) fixes.push('Add 2 recent comps and how you differ.');
      if (len < 35) fixes.push('Add 1–2 sentences with specific detail (names, numbers).');

      return { score: Math.max(0, Math.min(100, score)), strengths, fixes, perCriterion };
    },
    localRecommendations(scored, rubric, style) {
      const weak = (scored.perCriterion || []).filter(c => c.score0to100 < 70).map(c => c.name);
      const bullets = [];
      if (weak.some(n => /clarity|logline/i.test(n))) bullets.push('Rewrite your logline as **[Hero] must [do X] to [win Y], or else [bad thing]**.');
      if (weak.some(n => /hook|unique/i.test(n))) bullets.push('Add a **unique hook** clause: “Unlike other [genre] films, this …”.');
      if (weak.some(n => /stakes|urgency/i.test(n))) bullets.push('Spell out the **stakes** in one sentence starting with “If they fail…”');
      if (weak.some(n => /comps|audience/i.test(n))) bullets.push('Name **2 recent comps (≤5 yrs)** + 1 sentence on how you stand apart.');
      if (weak.some(n => /feasib|budget|plan/i.test(n))) bullets.push('Include a **budget band** and one **production constraint** with mitigation.');
      return `<h4>Recommendations (${style})</h4><ul>${bullets.map(b => `<li>${b}</li>`).join('')}</ul>`;
    },
    tryAgain() {
      if (!this.hasPathway) return;
      if (this.grades.length) {
        this.attempts.push({
          ts: Date.now(),
          totalScore: this.totalScore,
          style: this.pitchStyle,
          perQ: this.grades.slice(0)
        });
      }
      this.userAnswers = Array(this.pathway.length).fill('');
      this.grades = [];
      this.recommendations = [];
      this.currentIndex = 0;
      this.askCurrentQuestion();
    },
    archiveAttemptIfComplete() {
      if (this.hasPathway && this.grades.length === this.pathway.length && this.grades.every(g => g)) {
        this.attempts.push({
          ts: Date.now(),
          totalScore: this.totalScore,
          style: this.pitchStyle,
          perQ: this.grades.slice(0)
        });
      }
    },

    /* ======= Simulation (beats created on the fly) ======= */
    async startPitchSimulation() {
      const sys = `Simulate an interactive ${this.pitchStyle} pitch with producer mindset. Use short beats and a judge rubric per beat.`;
      const ctx =
        `TITLE: ${this.scriptTitle}\n` +
        `GENRE: ${this.scriptGenre}\n` +
        `EXCERPT:\n${this.scriptText.slice(0, 4000)}`;
      const user = 'Create a 6-beat flow as JSON array: [{ "title": "...", "coachPrompt": "...", "judgeRubric": { "criteria":[{"name","desc","weight"}], "tips":[...] } }]';

      let simPath = [];
      const raw = await this.askAI(
        [{ role: 'system', content: sys }, { role: 'user', content: ctx }, { role: 'user', content: user }],
        'gpt-4o'
      );
      if (raw) {
        const flow = this.safeParseJSON(raw);
        simPath = Array.isArray(flow) ? flow : [];
      }
      if (simPath.length) {
        this.pathway = simPath.map((b, i) => ({
          id: i + 1,
          q: `${b.title}: ${b.coachPrompt}`,
          html: `<p><strong>${b.title}:</strong> ${b.coachPrompt}</p>`,
          rubric: this._normalizeRubric(b.judgeRubric)
        }));
      } else {
        this.pathway = this._fallbackPath().map((b,i) => ({ id: i+1, q: `[Simulation] ${this.stripHtml(b.html)}`, html: b.html, rubric: this._normalizeRubric(b.rubric) }));
      }
      this.currentIndex = 0;
      this.userAnswers = Array(this.pathway.length).fill('');
      this.grades = [];
      this.recommendations = [];
      this.answer = `Simulation ready: ${this.pitchStyle} (${this.pathway.length} beats). I’ll read the first question—then start speaking.`;
      // Speak & listen
      this.askCurrentQuestion();
    },

    /* ======= Save / Load / Delete ======= */
    savePitch() {
      const rec = {
        id: this.currentPitch?.id || Date.now(),
        style: this.pitchStyle,
        scriptTitle: this.scriptTitle,
        scriptAuthor: this.scriptAuthor,
        scriptGenre: this.scriptGenre,
        scriptPages: this.scriptPages,
        scriptText: this.scriptText,
        pathway: this.pathway,
        userAnswers: this.userAnswers,
        grades: this.grades,
        recommendations: this.recommendations,
        attempts: this.attempts,
        updatedAt: Date.now()
      };
      if (!this.currentPitch) {
        this.savedPitches.unshift(rec);
        this.currentPitch = rec;
      } else {
        const idx = this.savedPitches.findIndex(p => p.id === this.currentPitch.id);
        if (idx >= 0) this.vueSet(this.savedPitches, idx, rec);
        else this.savedPitches.unshift(rec);
        this.currentPitch = rec;
      }
try { localStorage.setItem('savedPitches', JSON.stringify(this.savedPitches)); }
catch (e) { console.warn('saving pitches failed', e); }
      this.answer = 'Saved.';
    },
    loadPitches() {
try {
  const data = localStorage.getItem('savedPitches');
  if (data) this.savedPitches = JSON.parse(data);
} catch (e) { console.warn('loadPitches failed', e); }
    },
    deletePitch(id) {
      this.savedPitches = this.savedPitches.filter(p => p.id !== id);
      try { localStorage.setItem('savedPitches', JSON.stringify(this.savedPitches)); } catch (e) { console.warn('deleting pitches failed', e); }
      if (this.currentPitch?.id === id) {
        this.currentPitch = null;
        this.pathway = [];
        this.userAnswers = [];
        this.grades = [];
        this.recommendations = [];
        this.attempts = [];
        this.answer = '';
        this.currentIndex = -1;
      }
    },
    editPitch(p) {
      this.currentPitch = p;
      this.pitchStyle = p.style;
      this.scriptTitle = p.scriptTitle || '';
      this.scriptAuthor = p.scriptAuthor || '';
      this.scriptGenre = p.scriptGenre || '';
      this.scriptPages = p.scriptPages || 10;
      this.scriptText = p.scriptText || '';
      this.pathway = p.pathway || [];
      this.userAnswers = p.userAnswers || [];
      this.grades = p.grades || [];
      this.recommendations = p.recommendations || [];
      this.attempts = p.attempts || [];
      this.currentIndex = this.pathway.length ? 0 : -1;
      this.answer = 'Loaded.';
    },

    /* ======= Voice ======= */
    setupSpeech() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.voiceSupported = !!SR;
      if (!SR) return;
const rec = new SR();
rec.continuous = true;
rec.interimResults = true;
rec.lang = 'en-GB';   // <-- was 'en-US'

      rec.onresult = (e) => {
        let interim = '';
        let finalChunk = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const seg = e.results[i][0].transcript || '';
          if (e.results[i].isFinal) finalChunk += ' ' + seg;
          else interim += seg;
        }
        if (interim.trim()) {
  this.hasSpoken = true;
  this.lastSpeechAt = Date.now();
  this.scheduleSilenceWatch();
}
if (finalChunk.trim() && this.currentIndex >= 0) {
  const chunk = finalChunk.trim();
  if (/^skip\s+question$/i.test(chunk)) {
    this.hasSpoken = true;
    this.lastSpeechAt = Date.now();
    this.scheduleSilenceWatch();
    this.skipQuestion();
  } else {
    const cur = (this.userAnswers[this.currentIndex] || '').trim();
    this.userAnswers[this.currentIndex] = (cur ? cur + ' ' : '') + chunk;
    this.finalTranscript = chunk;
    this.hasSpoken = true;
    this.lastSpeechAt = Date.now();
    this.scheduleSilenceWatch();
  }
}

        this.interimTranscript = interim.trim();
        // Live coach updates while overlay is open
if (this.coachOverlayOpen) {
  this.updateMatchScore();
}

      };
      rec.onend = () => { this.recognizing = false; };
      rec.onerror = () => { this.recognizing = false; };
      this.recognition = rec;
    },
startMic() {
  if (!this.voiceSupported || !this.recognition) return;
  try {
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.recognition.start();
    this.recognizing = true;
    this.expectingAnswer = true;     // pulse on
  } catch (e) {
    this.recognizing = false;
    this.expectingAnswer = false;
  }
},
stopMic() {
  if (!this.voiceSupported || !this.recognition) return;
  try { this.recognition.stop(); } catch (e) { /* ignore */ }
  finally { this.recognizing = false; this.expectingAnswer = false; }  // pulse off
},

    toggleVoice() {
      if (this.recognizing) this.stopMic();
      else this.startMic();
    },

    /* ======= TTS ======= */
async speak(text) {
  if (!('speechSynthesis' in window)) return;
  try { window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }

  const safe = String(text || '').replace(/https?:\/\/[^\s)]+/g, (m) => {
    try { return new URL(m).host; } catch { return m; }
  });

  const utter = new SpeechSynthesisUtterance(safe);
  utter.lang = 'en-GB';                 // force UK locale
  utter.rate = this.ttsRate;
  utter.pitch = this.ttsPitch;

  // Ensure we have a voice (handles async loading on some browsers)
  if (!this.selectedVoice) {
    const voices = window.speechSynthesis.getVoices() || [];
    this.selectedVoice = this.pickVoiceFrom(voices);
  }
  if (this.selectedVoice) utter.voice = this.selectedVoice;

  return new Promise((resolve) => {
    utter.onend = () => { this.ttsSpeaking = false; resolve(); };
    utter.onerror = () => { this.ttsSpeaking = false; resolve(); };
    this.ttsSpeaking = true;
    window.speechSynthesis.speak(utter);
  });
},


    /* ======= Viz (simple) ======= */
    initViz() {
      const cvs = this.$refs.vizCanvas;
      if (!cvs) return;
      const ctx = cvs.getContext('2d');
      this.viz.ctx = ctx;

      const resize = () => {
        const dpr = this.viz.dpr;
        const w = Math.min(700, Math.floor(cvs.parentElement?.clientWidth || 520));
        const h = 120;
        cvs.width = w * dpr;
        cvs.height = h * dpr;
        cvs.style.width = w + 'px';
        cvs.style.height = h + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      window.addEventListener('resize', resize);

      const loop = () => {
        this.renderViz();
        this.viz.raf = requestAnimationFrame(loop);
      };
      loop();
    },
    renderViz() {
      const cvs = this.$refs.vizCanvas;
      const ctx = this.viz.ctx;
      if (!cvs || !ctx) return;

      const W = cvs.width / this.viz.dpr;
      const H = cvs.height / this.viz.dpr;
      ctx.clearRect(0, 0, W, H);

      const mid = H / 2;
      const state = this.recognizing ? 'mic' : (this.ttsSpeaking ? 'tts' : 'idle');
      const amp = state === 'mic' ? 26 : state === 'tts' ? 16 : 8;
      const speed = state === 'mic' ? 0.035 : state === 'tts' ? 0.02 : 0.01;
      const t = performance.now() * speed;

      const colors = [
        'rgba(203,36,128,0.75)',
        'rgba(41,200,192,0.75)',
        'rgba(24,137,218,0.75)'
      ];

      ctx.lineWidth = 2;
      colors.forEach((c, k) => {
        ctx.beginPath();
        ctx.strokeStyle = c;
        for (let x = 0; x <= W; x += 6) {
          const y = mid + Math.sin((x + k * 50 + t * 240) * 0.02) * (amp - k * 4);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      });
    },
  }
};
</script>
<style scoped>
/* Overlay */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

.overlay {
  position: fixed; inset: 0; z-index: 30;
  display: grid; place-items: center;
  background: rgba(8, 10, 20, 0.55);
  backdrop-filter: blur(3px);
}

.overlay-card {
  width: min(960px, 92vw);
  max-height: 82vh;
  overflow: auto;
  padding: 18px 18px 16px;
  border-radius: 18px;
}

.overlay-head { display:flex; align-items:center; justify-content:space-between; margin-bottom: 8px; }
.o-title { font-size: 18px; font-weight: 800; letter-spacing:.3px; }
.o-sub { opacity: .8; margin-top: 2px; }

.o-meta { display:flex; flex-wrap:wrap; gap:8px; margin: 6px 2px 10px; }
.o-chip { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12); padding: 4px 8px; border-radius: 999px; font-weight: 700; }
.o-chip .good { color:#34d399; }
.o-chip .ok { color:#fbbf24; }

.o-question { background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.1); padding:10px 12px; border-radius:12px; margin-bottom: 10px; }

.o-suggestions { display:grid; grid-template-columns: 1fr; gap: 10px; }
.sugg {
  display:flex; gap:10px; padding:10px; border-radius:12px;
  border:1px solid rgba(255,255,255,.12); background: rgba(255,255,255,.03);
  cursor:pointer;
}
.sugg.active { outline: 2px solid rgba(99,102,241,.6); background: rgba(99,102,241,.12); }
.sugg input { margin-top:4px; }
.sugg-body { display:flex; flex-direction:column; gap:8px; }
.sugg-text { font-weight:700; }
.sugg-keywords { display:flex; gap:6px; flex-wrap:wrap; }
.kw { font-size:12px; padding:2px 8px; border-radius:999px; background: rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); }
.kw.hit { background: rgba(16,185,129,.25); border-color: rgba(16,185,129,.5); }

.o-actions { display:flex; gap:8px; justify-content:flex-end; margin-top: 12px; }

/* Layout */
.app-wrap { position: relative; min-height: 100vh; color: #e9eefc; }
.bg-gradient {
  position: fixed; inset: 0;
  background:
    radial-gradient(1200px 800px at 10% 0%, #4f46e5 0%, transparent 60%),
    radial-gradient(1000px 700px at 90% 30%, #06b6d4 0%, transparent 60%),
    radial-gradient(1000px 700px at 50% 100%, #ec4899 0%, transparent 60%),
    linear-gradient(180deg, #0b1021 0%, #0b132b 60%, #0b132b 100%);
  z-index: 0;
  filter: saturate(120%);
}
.bg-texture { position:fixed; inset:0; pointer-events:none; background: repeating-radial-gradient(closest-side, rgba(255,255,255,.02), rgba(255,255,255,.02) 2px, transparent 3px); mix-blend-mode: overlay; }

.glass {
  background: rgba(17, 25, 40, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin: 30px 30px 30px 30px;
  padding: 15px 15px 30px 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
}
/* Ensure left/right placement and good wrapping on small screens */
/* Header: brand left, controls right */
.nav{
  display:flex;                 /* ensure flex */
  align-items:center;
  justify-content:flex-start;   /* left-align row; we'll push the right side with margin-left:auto */
  gap:12px;
}

.nav .brand{
  display:flex;
  align-items:center;           /* fix: 'align-items: left' is invalid */
  gap:10px;
  min-width:0;                  /* prevent overflow issues */
}

.nav .right{
  margin-left:auto;             /* pushes this block to the far right */
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;               /* wrap controls on small screens */
}

/* Small screens: stack controls under the brand neatly */
@media (max-width: 640px){
  .nav{
    flex-wrap:wrap;
    row-gap:8px;
  }
  .nav .right{
    margin-left:0;
    width:100%;
    justify-content:flex-start;
  }
}


.brand .logo { filter: drop-shadow(0 0 10px rgba(255,255,255,.3)); font-size: 22px; }
.brand h1 { font-size: 18px; margin: 0; font-weight: 700; letter-spacing: .4px; }

.left { display:flex; gap: 8px; align-items: center; flex-wrap: wrap; }

.main {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 20px;
  margin: 16px 12px;
}
@media (max-width: 1000px) {
  .main { grid-template-columns: 1fr; }
}

.pane { padding: 30px 25px 30px 30px;  }

/* Controls */
button { cursor: pointer; border: none; border-radius: 12px; padding: 10px 14px; color: #0b1021; background: #e9eefc; font-weight: 700; }
button.ghost { background: transparent; color: #ffffff; border: 1px solid rgba(255,255,255,.2); }
button.secondary { background: #a5b4fc; }
button.primary { background: #60a5fa; }
button.danger { background: #fda4af; color: #3a0b0f; }
button.sm { padding: 16px 10px; font-size: 12px; }
button[disabled] { opacity: .6; cursor: not-allowed; }

label { display:flex; flex-direction: column; gap: 6px; font-size: 13px; font-weight: 600; }
input, select, textarea {
  background: rgba(11,16,33,0.5); color: #e9eefc;
  border: 1px solid rgba(255,255,255,.12); padding: 10px 12px; border-radius: 12px;
}
textarea { resize: vertical; }

/* Sections */
.section-title { margin: 10px 10px 40px 10px; padding: -15px 10px 10px 10px; font-size: 18px; font-weight: 800; letter-spacing: .3px; }
.grid { display:grid; grid-template-columns: repeat(auto-fit,minmax(210px,1fr)); gap: 10px; margin-bottom: 10px; }
.row { display:flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.mt { margin-top: 12px; }
.muted { color: #c7d2fe; opacity: .8; }

/* Saved list */
.saved { padding: 10px; }
.save-row { display: flex; align-items: center; justify-content: space-between; border: 1px solid rgba(255,255,255,.15); border-radius: 12px; padding: 8px 10px; margin-bottom: 8px; }
.save-meta .title { font-weight: 800; }
.save-actions { display:flex; gap: 8px; }

/* Voice */
.flexbar { display:flex; justify-content: space-between; align-items: center; gap: 20px; margin: -10px 10px 10px 10px; }
.mic { display:flex; align-items: center; gap: 12px; }
/* ensure button is visible and styled */
.mic-btn { position: relative; z-index: 2; }

/* Per-step color classes */
.mic-btn.mic-c-default{ background:#e9eefc; color:#0b1021; }
.mic-btn.mic-c-blue   { background:#60a5fa; color:#0b1021; }
.mic-btn.mic-c-teal   { background:#2dd4bf; color:#0b1021; }
.mic-btn.mic-c-pink   { background:#f472b6; color:#2a0e1f; }
.mic-btn.mic-c-amber  { background:#fbbf24; color:#231a00; }
.mic-btn.mic-c-lime   { background:#a3e635; color:#1a2300; }
.mic-btn.mic-c-purple { background:#a78bfa; color:#120b2a; }

.viz { display:block; border-radius: 12px; background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.08); }
.voice-caption { margin-top: 10px; }
.voice-caption .cap { display:flex; gap: 8px; align-items:center; background: rgba(255,255,255,.06); padding: 8px 10px; border-radius: 12px; border: 1px solid rgba(255,255,255,.1); }
.pill { background: rgba(99,102,241,.3); padding: 2px 8px; border-radius: 999px; font-weight: 800; font-size: 12px; }
.pill.on { background: rgba(16,185,129,.35); }
.interim { opacity: .8; }
.final { font-weight: 700; }

/* Q&A */
.qa-head { display:flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.progress { opacity: .9; }
.avg { opacity: .9; }
.avg .pass { margin-left: 6px; background: rgba(16,185,129,.35); padding: 2px 8px; border-radius: 999px; }
.q-text { font-size: 16px; margin: 8px 0 6px; }
.tips { margin: 6px 0 10px; padding-left: 18px; }
.plain { margin: 6px 0 0; padding-left: 18px; }
.grade { margin-top: 12px; border-top: 1px solid rgba(255,255,255,.15); padding-top: 10px; }
.grade h3 { margin: 6px 0; }
.grade h3 .good { color: #34d399; }
.cols { display:grid; grid-template-columns: repeat(auto-fit,minmax(210px,1fr)); gap: 10px; }
.table { width: 100%; border-collapse: collapse; margin-top: 8px; }
.table th, .table td { border: 1px solid rgba(255,255,255,.12); padding: 8px; vertical-align: top; }
.table th { background: rgba(255,255,255,.06); text-align: left; }
.table .center { text-align: center; }
.table .wrap { white-space: normal; word-break: break-word; }
.table.small th, .table.small td { padding: 6px; font-size: 13px; }

/* Footer */
.foot { margin: 20px 35px 20px 35px; padding: 20px 20px 20px 20px; text-align: center; }

.coach-banner {
  margin-top: 10px; padding: 10px 12px; border-radius: 12px;
  background: rgba(99,102,241,.18); border: 1px solid rgba(255,255,255,.15);
  opacity: 0; transform: translateY(-4px); transition: .25s ease;
}
.coach-banner.show { opacity: 1; transform: translateY(0); }
.coach-banner .micpill { background: rgba(96,165,250,.25); border: 1px solid rgba(255,255,255,.18); border-radius: 999px; padding: 2px 8px; }

.submit-warning {
  margin-top: 8px; padding: 8px 10px; border-radius: 10px;
  background: rgba(234,179,8,.22); border: 1px solid rgba(234,179,8,.35);
  font-weight: 700;
}

.switch { display:flex; align-items:center; gap:8px; }
.switch input { width:16px; height:16px; }

/* -----------------------------
   NEW: KPI tiles + Progress bar
   ----------------------------- */
.kpi-grid {
  display:grid;
  grid-template-columns: repeat(auto-fit,minmax(180px,1fr));
  gap:12px;
}
.kpi {
  display:flex;
  align-items:center;
  justify-content:space-between;
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 12px;
  padding: 10px 12px;
}
.kpi .kpi-title {
  font-size:12px;
  opacity:.85;
}
.kpi .kpi-value {
  font-weight:800;
  font-size:18px;
  line-height:1.1;
}
.kpi .pill { margin-left: 8px; }

/* Progress bar */
.progressbar {
  height:10px;
  border-radius:999px;
  background:rgba(255,255,255,.06);
  border:1px solid rgba(255,255,255,.12);
  overflow:hidden;
}
.progressbar-fill {
  height:100%;
  background: linear-gradient(90deg,#60a5fa,#a78bfa);
  width:0%;
  transition: width .25s ease;
}

/* -----------------------------
   NEW: Delivery Grade badge
   (content × delivery)
   ----------------------------- */
.delivery-wrap {
  display:inline-flex;
  align-items:center;
  gap:6px;
}
.delivery-badge {
  display:inline-flex;
  align-items:center;
  gap:6px;
  border-radius:999px;
  padding:2px 8px;
  font-size:12px;
  font-weight:800;
  border:1px solid rgba(255,255,255,.18);
  background:rgba(255,255,255,.06);
  white-space:nowrap;
}
.delivery-badge::before {
  content:'🎤';
  font-size:12px;
}
.delivery-badge.good   { background: rgba(16,185,129,.22); border-color: rgba(16,185,129,.45); }
.delivery-badge.ok     { background: rgba(234,179,8,.22);  border-color: rgba(234,179,8,.45);  }
.delivery-badge.bad    { background: rgba(239,68,68,.22);  border-color: rgba(239,68,68,.45);  }

/* micro variant for tight tables */
.delivery-badge.mini { padding:2px 6px; font-size:11px; }

/* Optional: color by data-score attribute (0–100) if you don’t want classes) */
.delivery-badge[data-score] {
  /* default */
}
.delivery-badge[data-score]:where([data-score^="9"],[data-score^="8"]) { background: rgba(16,185,129,.22); border-color: rgba(16,185,129,.45); }
.delivery-badge[data-score^="7"] { background: rgba(234,179,8,.22); border-color: rgba(234,179,8,.45); }
.delivery-badge[data-score^="6"],
.delivery-badge[data-score^="5"],
.delivery-badge[data-score^="4"],
.delivery-badge[data-score^="3"],
.delivery-badge[data-score^="2"],
.delivery-badge[data-score^="1"],
.delivery-badge[data-score^="0"] { background: rgba(239,68,68,.22); border-color: rgba(239,68,68,.45); }

/* Tweak: rubric quick-tiles */
.crit-tile {
  border:1px solid rgba(255,255,255,.12);
  border-radius:12px;
  padding:8px 10px;
  background:rgba(255,255,255,.04);
}

@media (max-width: 560px) {
  .kpi-grid { grid-template-columns: 1fr 1fr; }
}
</style>
